import React, { useEffect, useState, useMemo } from "react";
import { CaretRight } from "@phosphor-icons/react";
import {
  Carousel as ShadcnCarousel,
  CarouselContent,
  CarouselItem,
} from "@shopflo/ui/carousel";
import type { CarouselApi } from "@shopflo/ui/carousel";
import { classNames } from "../helpers";

export interface ListCarouselControls {
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
}

export interface ListCarouselProps {
  count: number;
  children?: React.ReactNode;
  customCSS?: string;
  shouldRenderDots?: boolean;
  navButton?: React.ReactElement;
  hideNavButtons?: boolean;
  onControlsChange?: (controls: ListCarouselControls) => void;
  /**
   * `"padding"` (default) — legacy per-slide `pl-8` spacing used by dashboard
   * previews and DiscountedProductCard.
   * `"gap"` — cancel CarouselContent's `-ml-4` and use gap so absolutely
   * positioned card dividers sit between slides (UpSellSection).
   */
  slideSpacing?: "padding" | "gap";
}

const DefaultNavButton = (
  <button className="ui-flex ui-h-6 ui-w-6 ui-items-center ui-justify-center ui-rounded-full ui-border-[1px] ui-border-carbon-lighter ui-bg-white/70 ui-text-coal-dark ui-backdrop-blur-[2px]">
    <CaretRight className="ui-h-4 ui-w-4" />
  </button>
);

export const ListCarousel: React.FC<ListCarouselProps> = ({
  count,
  children,
  customCSS,
  shouldRenderDots = true,
  navButton = DefaultNavButton,
  hideNavButtons = false,
  onControlsChange,
  slideSpacing = "padding",
}) => {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const updateState = () => {
      setCurrent(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    updateState();
    api.on("select", updateState);
    api.on("reInit", updateState);

    return () => {
      api.off("select", updateState);
      api.off("reInit", updateState);
    };
  }, [api]);

  const scrollByOneCard = (direction: "prev" | "next") => {
    if (!api) return;

    const slides = api.slideNodes();
    if (slides.length === 0) return;

    // Get current selected snap index
    const currentIndex = api.selectedScrollSnap();

    // Calculate target index
    const targetIndex =
      direction === "prev"
        ? Math.max(0, currentIndex - 1)
        : Math.min(slides.length - 1, currentIndex + 1);

    // Scroll to the target card
    api.scrollTo(targetIndex);
  };

  useEffect(() => {
    onControlsChange?.({
      scrollPrev: () => scrollByOneCard("prev"),
      scrollNext: () => scrollByOneCard("next"),
      canScrollPrev,
      canScrollNext,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, canScrollPrev, canScrollNext]);

  const renderDots = useMemo(() => {
    const dots: JSX.Element[] = [];
    for (let index = 0; index < count - 1; index++) {
      dots.push(
        <div
          key={`Item-${index}`}
          onClick={() => api?.scrollTo(index)}
          className={`ui-h-2 ui-w-2 ui-cursor-pointer ui-snap-center ui-rounded-full ${index === current ? "ui-bg-primary-dark" : "ui-bg-gray-light"
            }`}
        ></div>
      );
    }
    return dots;
  }, [count, current, api]);

  // Convert children to array
  const childrenArray = React.Children.toArray(children);

  if (!childrenArray.length) return null;

  return (
    <div className="ui-w-full">
      <div className={classNames("ui-relative", "ui-mb-3")}>
        <ShadcnCarousel
          opts={{
            align: "start",
            slidesToScroll: 1,
            dragFree: true,
          }}
          className="ui-w-full"
          setApi={setApi}
        >
          {!hideNavButtons && canScrollPrev && (
            <div
              onClick={(e) => {
                e?.stopPropagation();
                scrollByOneCard("prev");
              }}
              className="ui-absolute ui-z-10 -ui-left-2 ui-top-1/3 -ui-translate-y-1/2 ui-rotate-180 ui-cursor-pointer"
            >
              {navButton}
            </div>
          )}

          <CarouselContent
            className={classNames(
              slideSpacing === "gap"
                ? // Cancel CarouselContent's default -ml-4; use gap so card dividers
                  // (right-[-16px]) sit in the space BETWEEN slides, not under the next card.
                  "!ui-ml-0 ui-gap-8 ui-pl-4 ui-scrollbar-hide [&>div:last-child]:!ui-pr-4"
                : "ui-scrollbar-hide [&>div:last-child]:!ui-pr-4 [&>div:first-child]:!ui-pl-4",
              customCSS || "",
            )}>
            {childrenArray.map((child, index) => (
              <div
                className={slideSpacing === "gap" ? "ui-shrink-0" : "ui-pl-8"}
                key={index}>
                {child}
              </div>
            ))}
          </CarouselContent>

          {!hideNavButtons && count > 1 && canScrollNext && (
            <div
              onClick={(e) => {
                e?.stopPropagation();
                scrollByOneCard("next");
              }}
              className="ui-absolute ui-z-10 -ui-right-2 ui-top-1/3 -ui-translate-y-1/2 ui-cursor-pointer"
            >
              {navButton}
            </div>
          )}
        </ShadcnCarousel>
      </div>
      {shouldRenderDots ? (
        <div className="ui-flex ui-snap-x ui-snap-mandatory ui-items-center ui-justify-center ui-space-x-1 ui-p-4 ui-pt-[10px]">
          {renderDots}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
